import type * as React from "react"
import { useTranslation } from "react-i18next"
import { isFeatureOff, useOutstanding } from "@/features/payments/use-payments"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/features/auth/auth-context"

/**
 * "All bills / Still owed" — the payment filter on the bill register (§17.5).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT LOOKS LIKE THE STATUS FILTER BESIDE IT AND BEHAVES DIFFERENTLY, WHICH IS
 * WHY IT IS ITS OWN CONTROL RATHER THAN AN EXTRA OPTION IN THAT SELECT.
 *
 * A payment state is not a document status (§17.5): a bill is ISSUED *and* part
 * paid. Folding "Unpaid" into the status list would put the two on one axis and
 * make "Issued and unpaid" unaskable — the exact overloading §17.5 refuses in
 * the database. Two selects, two questions.
 *
 * CHOOSING "STILL OWED" CLEARS EVERY OTHER FILTER, and that is honest rather
 * than convenient. The unpaid list comes from `GET /payments/outstanding`, which
 * takes no parameters at all — no dates, no customer, no amount range, no
 * paging — because "outstanding" is an aggregate over the payments table that
 * the invoice register's `where` cannot express. Leaving a date range visibly
 * "on" while it silently did nothing would be a filter that lies.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS RUNS A QUERY JUST TO DECIDE WHETHER TO RENDER ITSELF.
 *
 * There is no `useFlag` on the client and there must not be one: `GET /flags`
 * needs `settings.write`, which the OWNER does not hold, so asking it would 403
 * for the one person using this register. The only signal available is the 404
 * that `requireFlag("payments")` answers with (§18.3), and the only way to see
 * it is to ask.
 *
 * So it asks — for the list itself. The request is `useOutstanding()`, keyed and
 * cached exactly as the unpaid register's own, so the two share ONE cache entry:
 * switching the filter on paints from data already in hand rather than firing a
 * second request. A 404 hides this control, which is what makes a flagged-off
 * build show nothing of the feature on this screen rather than offering a filter
 * that would open a "not switched on" panel.
 *
 * The cost is one small request per visit to the register while the module is
 * on. It is unpaged by design but it is the UNPAID bills of a one-man workshop,
 * not the book; the alternative — showing the control and letting the owner
 * discover the feature is dark by tapping it — is the leak the 404 exists to
 * prevent.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function PaymentFilterSelect({
  unpaidOnly,
  onChange,
}: {
  unpaidOnly: boolean
  /** `true` switches the register to the outstanding list; `false` restores it. */
  onChange: (unpaidOnly: boolean) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can } = useAuth()

  const available = useOutstanding({ enabled: can("payment.view") })

  // §2.1 — a capability, never a role. Without it the request is never made.
  if (!can("payment.view")) return null
  // §18.3 — the module is off. Show nothing at all, not a disabled control.
  if (available.isError && isFeatureOff(available.error)) return null

  const options = [
    { value: "", label: t("list.anyPayment") },
    { value: "unpaid", label: t("list.onlyUnpaid") },
  ]

  return (
    <div className="flex min-w-40 flex-col gap-1.5">
      <Label htmlFor="filter-payment">{t("list.paymentFilter")}</Label>
      <Select
        items={options}
        value={unpaidOnly ? "unpaid" : ""}
        onValueChange={(value) => {
          onChange(value === "unpaid")
        }}
      >
        {/* The id lands on the trigger button, which `<Label htmlFor>` then
          * labels — a <button> is a labelable element, so `getByLabelText`
          * finds this control. */}
        <SelectTrigger id="filter-payment" className="w-full">
          <SelectValue placeholder={t("list.anyPayment")} />
        </SelectTrigger>
        <SelectContent className="w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} className="pl-3">
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
